import { RealtimeEvent, RealtimeEventType, realtimeEventTypes } from 'fym-shared/src/music24/realtimeEvents';

/** A cache tag of `fymAPI`: a whole tag type, or one entry of it. */
export type RealtimeCacheTag = string | { type: string; id: string };

/** The id of the release kit list tag (`RELEASE_KIT_LIST_ID` in `fymAPI.ts`). */
const RELEASE_KIT_LIST_ID = 'LIST';

/**
 * The one place that says which `fymAPI` cache tags an event makes stale. The provider
 * invalidates them, and RTK Query reads each query that a mounted screen uses again, through the
 * normal REST routes. So a screen needs no socket code to stay fresh.
 *
 * An event of another artist of the same workspace invalidates the same tags. That costs one
 * read of the open screen and shows no wrong data: each query has the account id in its address.
 *
 * To add an event: add it to `fym-shared/src/music24/realtimeEvents.ts`, emit it on the server,
 * and add its case here. TypeScript refuses a missing case.
 */
export function realtimeEventTags(event: RealtimeEvent): RealtimeCacheTag[] {
  switch (event.type) {
    case 'promo.channel.synced':
      // The channel cards, the Home social summary, the channel numbers and the post lists. The
      // Calendar shows the channel state and the free slots of the channels.
      return ['PromoChannels', 'PromoSocialNumbers', 'PromoCalendar'];
    case 'promo.post.updated':
      // The post lists, the Calendar and the performance numbers of the posts. A schedule or a
      // cancel of another member changes the scheduled posts left this month. The Releases list and
      // page count the posts of each release.
      return ['PromoPosts', 'PromoCalendar', 'PromoPerformance', 'PromoQuota', 'Releases'];
    case 'promo.posts.changed':
      // Many posts changed in one request (a bulk add or a bulk schedule).
      return ['PromoPosts', 'PromoCalendar', 'PromoPerformance', 'PromoQuota', 'Releases'];
    case 'reel.updated':
      // The reel, the reel lists (a new reel is in no list yet, so the whole type), and the song
      // page and the release list, which show the reels of a song. The reel and kit queries read
      // the video credits again by themselves when something failed (`fymAPI.ts`).
      return [
        'Reels',
        ...(event.catalogItemUuid === null
          ? []
          : [
              { type: 'ReleaseKit', id: event.catalogItemUuid },
              // The rollout plan shows the reel of a step and its render percent. The dry run of the
              // AI reels plan has its own tag (`ReelsPlanPreview`): this event does not send it again.
              { type: 'Rollout', id: event.catalogItemUuid },
            ]),
        { type: 'ReleaseKit', id: RELEASE_KIT_LIST_ID },
        // The "N working" / "N failed" badge of the Assets menu entry.
        'Work',
        // The Releases list counts and "N new" (Releases v2).
        'Releases',
        // A clean copy of a reel is ready or failed (trial upsell, design b5).
        'CleanCopies',
      ];
    case 'kit.asset.updated':
      // The song page, the Studio result and the release list. The Calendar shows the file and
      // the poster of a post. The Assets page reads its own tRPC pages on this event; its menu
      // badge reads the `Work` tag.
      return [
        { type: 'ReleaseKit', id: event.catalogItemUuid },
        { type: 'ReleaseKit', id: RELEASE_KIT_LIST_ID },
        // The ProRes download of a motion artwork file (design w) is ready or failed.
        { type: 'KitProRes', id: event.assetUuid },
        'PromoCalendar',
        'Work',
        'Releases',
        // A clean copy of a kit asset is ready or failed (trial upsell, design b5).
        'CleanCopies',
      ];
    case 'catalog.song.updated':
      // The catalog table, the song files sheet, the master player and the Studio's song field.
      // The song page reads the master state too. The Calendar shows the release day and the cover.
      // A new cover gives the kit new colours: the
      // Studio song cards (the kit list) draw the song in them.
      return [
        { type: 'CatalogItem', id: event.catalogItemUuid },
        { type: 'ReleaseKit', id: event.catalogItemUuid },
        { type: 'ReleaseKit', id: RELEASE_KIT_LIST_ID },
        'PromoCalendar',
        // The setup check of the release reads the master and the lyrics.
        'Releases',
      ];
    case 'kit.zip.updated':
      // The "Download the zip" link of the song page that asked for this zip.
      return [{ type: 'KitZip', id: event.requestId }];
    case 'artistPortal.synced':
      // A read of an artist portal or of Ditto started or ended. The Streaming screen, the Home
      // numbers, the Catalog cards and the navigation badge read the numbers and the state again.
      // The Spotify Canvas rows show the Spotify for Artists sign-in (Connect, Reconnect).
      return [
        'Streaming',
        'ArtistPortalOverview',
        'ArtistPortalTracks',
        'ServiceConnection',
        'ConnectionInsight',
        'SpotifyCanvas',
      ];
    case 'cover.picture.updated':
      // The AI pictures of the cover editor of the song, and the month count (a failed picture
      // gives its unit back).
      return [{ type: 'CoverEditor', id: event.catalogItemUuid }];
    case 'asset.publish.updated':
      // The one-off channel badges (YouTube, Spotify Canvas) of the asset cards of the account,
      // and the row "Spotify Canvas" of "Where it plays" of the song (design spotify-canvas a2).
      return [
        { type: 'AssetPublish', id: 'LIST' },
        { type: 'SpotifyCanvas', id: event.catalogItemUuid },
      ];
    case 'song.publish.updated':
      // A song upload (SoundCloud) changed: the "Where it plays" panel of the song and the upload
      // count of the SoundCloud card. A live upload can write the SoundCloud link of the song.
      return [
        { type: 'SongPublish', id: event.catalogItemUuid },
        { type: 'SongPublish', id: 'LIST' },
        { type: 'CatalogItem', id: event.catalogItemUuid },
        'ServiceConnection',
      ];
    case 'presave.release.updated':
      // The subscriber numbers of the Fans screen and the "Send to subscribers" state of the song.
      return event.catalogItemUuid === null
        ? ['FansSummary', 'FansReleases', 'PresaveCampaign']
        : ['FansSummary', 'FansReleases', 'PresaveCampaign', { type: 'Releases', id: event.catalogItemUuid }];
    case 'musicVideo.cut.updated':
      // The director wrote a cut, or a member saved it: the document of the cut, and the cut list
      // of the editor (the cut menu, the director state).
      return [
        { type: 'MusicVideoCut', id: event.cutUuid },
        { type: 'MusicVideoEditor', id: event.catalogItemUuid },
      ];
    case 'musicVideo.directed.updated':
      // A directed video changed (status, director, cast, clip, render progress): the video and the
      // directed video list of the song.
      return [
        { type: 'DirectedVideo', id: event.directedVideoUuid },
        { type: 'DirectedVideoList', id: event.catalogItemUuid },
      ];
    case 'footage.clip.updated':
      // An AI clip or an upload of the account changed: the footage lists of the editor.
      return ['Footage'];
    case 'profile.asset.updated':
      // A version of an artist Profile image changed (made, failed, a Spotify upload, the manual
      // mark): Studio → Profile, its version lists, the Studio entry card and the Kit tab shortcut.
      return ['ArtistProfile'];
    case 'profile.send.updated':
      // A Profile "Send to all" changed: the send and the sheet (both under the Profile tag).
      return ['ArtistProfile'];
    case 'outreach.profile.updated':
    case 'outreach.campaign.updated':
    case 'outreach.message.updated':
    case 'outreach.reply.received':
    case 'outreach.placement.found':
    case 'outreach.mailbox.updated':
    case 'outreach.find.progress':
      // Outreach reads tRPC only, no `fymAPI` tag: `useOutreachRealtime` invalidates the
      // `outreach.*` queries of each event (also `campaigns.live` and `campaigns.activity`).
      return [];
    default: {
      const unknown: never = event;
      return unknown;
    }
  }
}

const sampleEvents: Record<RealtimeEventType, RealtimeEvent> = {
  'promo.channel.synced': { type: 'promo.channel.synced', accountId: 1, platform: 'tiktok' },
  'promo.post.updated': { type: 'promo.post.updated', accountId: 1, postUuid: '-' },
  'promo.posts.changed': { type: 'promo.posts.changed', accountId: 1 },
  'reel.updated': { type: 'reel.updated', accountId: 1, reelUuid: '-', catalogItemUuid: null },
  'kit.asset.updated': { type: 'kit.asset.updated', accountId: 1, catalogItemUuid: '-', assetUuid: '-' },
  'catalog.song.updated': { type: 'catalog.song.updated', accountId: 1, catalogItemUuid: '-' },
  'kit.zip.updated': { type: 'kit.zip.updated', accountId: 1, catalogItemUuid: '-', requestId: '-' },
  'artistPortal.synced': { type: 'artistPortal.synced', accountId: 1, integrationType: '-' },
  'cover.picture.updated': { type: 'cover.picture.updated', accountId: 1, catalogItemUuid: '-', runUuid: '-' },
  'asset.publish.updated': {
    type: 'asset.publish.updated',
    accountId: 1,
    catalogItemUuid: '-',
    assetUuid: '-',
    publishUuid: '-',
  },
  'song.publish.updated': { type: 'song.publish.updated', accountId: 1, catalogItemUuid: '-', publishUuid: '-' },
  'presave.release.updated': {
    type: 'presave.release.updated',
    accountId: 1,
    releaseUuid: '-',
    catalogItemUuid: null,
  },
  'musicVideo.cut.updated': { type: 'musicVideo.cut.updated', accountId: 1, catalogItemUuid: '-', cutUuid: '-' },
  'musicVideo.directed.updated': {
    type: 'musicVideo.directed.updated',
    accountId: 1,
    catalogItemUuid: '-',
    directedVideoUuid: '-',
  },
  'footage.clip.updated': { type: 'footage.clip.updated', accountId: 1, clipUuid: '-' },
  'profile.asset.updated': { type: 'profile.asset.updated', accountId: 1, assetUuid: '-', kind: '-' },
  'profile.send.updated': { type: 'profile.send.updated', accountId: 1, sendUuid: '-' },
  'outreach.profile.updated': { type: 'outreach.profile.updated', accountId: 1, profileUuid: '-' },
  'outreach.campaign.updated': { type: 'outreach.campaign.updated', accountId: 1, campaignUuid: '-' },
  'outreach.message.updated': { type: 'outreach.message.updated', accountId: 1, campaignUuid: '-', messageUuid: '-' },
  'outreach.reply.received': { type: 'outreach.reply.received', accountId: 1, campaignUuid: '-', replyUuid: '-' },
  'outreach.placement.found': { type: 'outreach.placement.found', accountId: 1, campaignUuid: '-', placementUuid: '-' },
  'outreach.mailbox.updated': { type: 'outreach.mailbox.updated', accountId: 1, mailboxUuid: '-' },
  'outreach.find.progress': { type: 'outreach.find.progress', accountId: 1 },
};

/**
 * Each tag type that an event can make stale. The provider invalidates all of them when the
 * socket connects again after a break: an event may have gone by meanwhile.
 */
export const realtimeTagTypes: string[] = [
  ...new Set(
    realtimeEventTypes.flatMap((type) =>
      realtimeEventTags(sampleEvents[type]).map((tag) => (typeof tag === 'string' ? tag : tag.type))
    )
  ),
];
